// Row contracts and jsonb shapes of the finance views (M5-03; 02 §Finance; 11 §Revenue
// attribution, §Planning). Same conventions as `rows.ts`: keys are column names, nullability is
// the column's, enums are the CHECK's; bound to their tables in `contract-bindings.ts`.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  CategoryKind,
  CompensationCadence,
  Currency,
  FinanceRecordStatus,
  ForecastSubjectKind,
  HeadcountHolderKind,
  RecurringCadence,
  RecurringExpenseKind,
  RevenueKind,
} from "./enums";
import { Decimal, IsoDate, LIMITS, MinorAmount, Timestamp, text, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };
const Month = z.string().regex(/^\d{4}-\d{2}$/, "a month key (2026-10)");

// jsonb shapes ----------------------------------------------------------------------------------

/** Where a revenue row came from (an invoice number, a billing system's id). */
export const FinanceSourceRef = z
  .strictObject({ system: text(LIMITS.key), id: text(LIMITS.name) })
  .meta({ id: "FinanceSourceRef" });
export type FinanceSourceRef = z.infer<typeof FinanceSourceRef>;

export const ScenarioKpiDelta = z.strictObject({
  metric_key: text(LIMITS.key),
  delta: z.number().finite(),
});

const SeatChange = {
  seat_template: text(LIMITS.name),
  count: z.int().min(1).max(1000),
  assumed_kpi_delta: z.array(ScenarioKpiDelta).max(20).optional(),
};

/**
 * One change of a scenario (11 §Planning): `add_seat`, `remove_seat`, `change_holder` ("hire a
 * human vs a DE for this seat"), `change_budget` (a company, team or seat budget for the base
 * month). `seat_template` names a group of seats: a role name, or a seat title for one seat.
 */
export const ScenarioChangeJson = z
  .discriminatedUnion("op", [
    z.strictObject({
      op: z.literal("add_seat"),
      ...SeatChange,
      holder_kind: HeadcountHolderKind,
      assumed_cost_minor: MinorAmount.min(0),
    }),
    z.strictObject({
      op: z.literal("remove_seat"),
      ...SeatChange,
      holder_kind: HeadcountHolderKind,
    }),
    z.strictObject({
      op: z.literal("change_holder"),
      ...SeatChange,
      from_holder_kind: HeadcountHolderKind,
      holder_kind: HeadcountHolderKind,
      assumed_cost_minor: MinorAmount.min(0),
    }),
    z.strictObject({
      op: z.literal("change_budget"),
      node_id: Uuid,
      amount_minor: MinorAmount.min(0),
    }),
  ])
  .meta({ id: "ScenarioChange" });
export type ScenarioChangeJson = z.infer<typeof ScenarioChangeJson>;

/** The engine's answer (`@companyos/domain-finance` `runScenario`), money in minor units. */
export const ScenarioResultJson = z
  .strictObject({
    base_period: Month,
    currency: Currency,
    cost: z.strictObject({
      base_minor: MinorAmount,
      scenario_minor: MinorAmount,
      delta_minor: MinorAmount,
    }),
    revenue_minor: MinorAmount,
    margin: z.strictObject({
      base_minor: MinorAmount,
      scenario_minor: MinorAmount,
      scenario_ratio: Decimal.nullable(),
    }),
    seats: z.array(
      z.strictObject({
        seat_template: z.string(),
        holder_kind: HeadcountHolderKind,
        count: z.int().min(0),
        cost_per_seat_minor: MinorAmount,
      }),
    ),
    budgets: z.record(z.string(), MinorAmount),
    kpis: z.array(
      z.strictObject({
        metric_key: z.string(),
        base_actual: z.number(),
        scenario_actual: z.number(),
        base_attainment: z.number(),
        scenario_attainment: z.number(),
      }),
    ),
  })
  .meta({ id: "ScenarioResult" });
export type ScenarioResultJson = z.infer<typeof ScenarioResultJson>;

// Rows --------------------------------------------------------------------------------------------

export const Category = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    kind: CategoryKind,
    code: z.string().nullable(),
    cost_kinds: z.array(z.string()),
    status: FinanceRecordStatus,
    ...actor,
    ...stamps,
  })
  .meta({ id: "Category" });
export type Category = z.infer<typeof Category>;

export const Client = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    status: FinanceRecordStatus,
    ...actor,
    ...stamps,
  })
  .meta({ id: "Client" });
export type Client = z.infer<typeof Client>;

export const ClientSeat = z
  .object({
    company_id: Uuid,
    client_id: Uuid,
    seat_id: Uuid,
    share: Decimal,
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "ClientSeat" });
export type ClientSeat = z.infer<typeof ClientSeat>;

export const RevenueEvent = z
  .object({
    company_id: Uuid,
    id: Uuid,
    occurred_at: Timestamp,
    period_key: Month,
    amount_minor: MinorAmount,
    currency: Currency,
    client_id: Uuid.nullable(),
    seat_id: Uuid.nullable(),
    project_ref: z.string().nullable(),
    kind: RevenueKind,
    category_id: Uuid.nullable(),
    source_ref: FinanceSourceRef.nullable(),
    note: z.string().nullable(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "RevenueEvent" });
export type RevenueEvent = z.infer<typeof RevenueEvent>;

export const Compensation = z
  .object({
    company_id: Uuid,
    id: Uuid,
    seat_id: Uuid,
    membership_id: Uuid.nullable(),
    amount_minor: MinorAmount,
    currency: Currency,
    cadence: CompensationCadence,
    effective_from: IsoDate,
    effective_to: IsoDate.nullable(),
    category_id: Uuid.nullable(),
    note: z.string().nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Compensation" });
export type Compensation = z.infer<typeof Compensation>;

export const RecurringExpense = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    kind: RecurringExpenseKind,
    amount_minor: MinorAmount,
    currency: Currency,
    cadence: RecurringCadence,
    seat_id: Uuid.nullable(),
    team_id: Uuid.nullable(),
    start_month: Month,
    end_month: Month.nullable(),
    category_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "RecurringExpense" });
export type RecurringExpense = z.infer<typeof RecurringExpense>;

export const ForecastSnapshot = z
  .object({
    company_id: Uuid,
    id: Uuid,
    subject_kind: ForecastSubjectKind,
    subject_id: Uuid,
    period_key: Month,
    as_of: IsoDate,
    actual_minor: MinorAmount,
    run_rate_minor: MinorAmount.nullable(),
    committed_minor: MinorAmount,
    projected_minor: MinorAmount,
    budget_minor: MinorAmount.nullable(),
    currency: Currency,
    created_at: Timestamp,
  })
  .meta({ id: "ForecastSnapshot" });
export type ForecastSnapshot = z.infer<typeof ForecastSnapshot>;

export const Scenario = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    base_period: Month,
    changes: z.array(ScenarioChangeJson),
    result: ScenarioResultJson.nullable(),
    evaluated_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Scenario" });
export type Scenario = z.infer<typeof Scenario>;

export const HeadcountPlan = z
  .object({
    company_id: Uuid,
    id: Uuid,
    title: z.string(),
    team_id: Uuid.nullable(),
    seat_id: Uuid.nullable(),
    holder_kind: HeadcountHolderKind,
    start_month: Month,
    end_month: Month.nullable(),
    monthly_cost_minor: MinorAmount,
    currency: Currency,
    status: FinanceRecordStatus,
    note: z.string().nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "HeadcountPlan" });
export type HeadcountPlan = z.infer<typeof HeadcountPlan>;
